import { QuoteWithDetails } from '../types/product';
import { pdfService } from './pdfService';

export interface ZeptoMailResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const zeptoMailService = {
  // Enviar cotización por correo usando Zoho ZeptoMail (vía backend)
  sendQuoteEmail: async (quote: QuoteWithDetails): Promise<ZeptoMailResponse> => {
    try {
      console.log('🚀 Iniciando envío de correo para cotización:', quote.id);
      
      // Generar el PDF como base64
      const pdfBase64 = await pdfService.generateQuotePDFBase64(quote);
      
      if (!pdfBase64) {
        console.error('❌ No se pudo generar el PDF de la cotización');
        return {
          success: false,
          message: 'No se pudo generar el PDF de la cotización'
        };
      }

      // Extraer información del cliente (priorizar cliente asociado)
      let customerName = quote.company || quote.name;
      let customerEmail = quote.email;
      
      // Si hay un cliente asociado, usar sus datos
      if ((quote as any).customers && (quote as any).customers.email) {
        customerName = (quote as any).customers.company || (quote as any).customers.name;
        customerEmail = (quote as any).customers.email;
        console.log('✅ Usando datos del cliente asociado:', {
          customerName,
          customerEmail,
          customerId: (quote as any).customers.id
        });
      } else {
        console.log('⚠️  No hay cliente asociado, usando email de cotización:', {
          customerName,
          customerEmail
        });
      }
      
      console.log('📧 Datos del correo:', {
        customerName,
        customerEmail,
        quoteId: quote.id
      });
      
      // Extraer el código de cotización del mensaje
      const messageParts = quote.message.split(' - ');
      let quoteCode = messageParts[0] || 'COTIZACIÓN';
      
      // Si el código contiene la palabra "Cotización", eliminarla para el asunto
      if (quoteCode.startsWith('Cotización ')) {
        quoteCode = quoteCode.replace('Cotización ', '');
      }

      // Obtener el nombre del primer producto para el asunto
      let productName = 'Productos';
      if (quote.quote_details && quote.quote_details.length > 0) {
        // Si tenemos detalles, intentamos obtener el nombre del primer producto
        // Nota: Esto requeriría una llamada adicional para obtener los nombres de los productos
        productName = quote.quote_details[0].notes || 'Producto';
      } else if (quote.product_name) {
        productName = quote.product_name;
      }

      // Preparar el contenido del correo
      const emailData = {
        to: customerEmail,
        subject: `Cotización de ${productName}`,
        htmlbody: `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <p>Estimados señores de ${customerName}:</p>
            
            <p>Por medio de la presente, nos complace presentarles la siguiente cotización para el suministro de ${productName} de alta calidad, adecuadas para sus procesos.</p>
            
            <p>Agradecemos de antemano su atención a esta propuesta y quedamos a su disposición para cualquier aclaración o para especificar las cantidades requeridas y proceder con la Orden de Compra.</p>
            
            <p>Atentamente,</p>
            <p><strong>Luz Espinoza Rengifo</strong><br>
            Asesora de Ventas<br>
            Atomic Corporation SRL</p>
          </div>
        `,
        attachments: [
          {
            content: pdfBase64,
            name: `${quoteCode}.pdf`,
            encoding: "base64"
          }
        ],
        customerName: quote.name,
        quoteCode: quoteCode
      };

      console.log('📤 Enviando correo al backend con datos:', {
        to: emailData.to,
        subject: emailData.subject,
        hasAttachment: !!emailData.attachments[0]
      });

      // Enviar el correo usando el endpoint del backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      
      // Determinar el protocolo basado en el origen actual
      const isHttps = window.location.protocol === 'https:';
      const protocol = isHttps ? 'https' : 'http';
      
      // Construir URL del backend con el mismo protocolo que el frontend
      let finalBackendUrl = backendUrl;
      if (isHttps && backendUrl.startsWith('http://')) {
        finalBackendUrl = backendUrl.replace('http://', 'https://');
      } else if (!isHttps && backendUrl.startsWith('https://')) {
        finalBackendUrl = backendUrl.replace('https://', 'http://');
      }
      
      console.log('🌐 Enviando solicitud al backend:', {
        originalUrl: backendUrl,
        finalUrl: finalBackendUrl,
        isHttps,
        protocol
      });
      
      const response = await fetch(`${finalBackendUrl}/send-quote-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailData)
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (jsonError) {
        console.error('❌ Error al parsear JSON del backend:', jsonError);
        const textResponse = await response.text();
        console.error('📄 Respuesta como texto:', textResponse);
        
        return {
          success: false,
          message: `Error del servidor: ${response.status} ${response.statusText}`,
          data: { textResponse, error: 'Invalid JSON response' }
        };
      }

      console.log('📨 Respuesta del backend:', {
        status: response.status,
        ok: response.ok,
        data: responseData
      });

      if (response.ok) {
        return {
          success: true,
          message: responseData.message || 'Correo enviado exitosamente',
          data: responseData
        };
      } else {
        return {
          success: false,
          message: responseData.message || responseData.error || `Error al enviar correo: ${response.statusText}`,
          data: responseData
        };
      }
    } catch (error) {
      console.error('❌ Error al enviar correo con ZeptoMail:', error);
      return {
        success: false,
        message: 'Error inesperado al enviar el correo'
      };
    }
  }
};